import { useState, useCallback, useRef } from 'react'
import {
  getAvailableCharacters,
  getTaskStatus
} from '../../../services/api/aiVideoGeneration'

/**
 * Custom hook to manage character fetching and state for VideoAi
 * Follows the same pattern as useImageGeneration and useVideoGeneration
 */
export const useCharacters = ({ onCharactersLoaded, videoId } = {}) => {
  const [characters, setCharacters] = useState([])
  const [isFetchingCharacters, setIsFetchingCharacters] = useState(false)
  const hasFetchedRef = useRef(false)

  const waitForTaskOutput = async taskId => {
    try {
      const statusRes = await getTaskStatus(taskId)
      const output = statusRes && statusRes.data && statusRes.data.output
      if (Array.isArray(output) && output.length > 0) {
        return output
      }
    } catch (err) {
      console.error('Error checking task status:', err)
    }

    await new Promise(resolve => setTimeout(resolve, 1000))
    return waitForTaskOutput(taskId)
  }

  const fetchCharacters = useCallback(
    async (force = false) => {
      // Skip if already fetched and not forcing
      if (hasFetchedRef.current && !force) {
        return characters
      }

      // Skip if already fetching
      if (isFetchingCharacters) {
        return characters
      }

      try {
        setIsFetchingCharacters(true)
        const response = await getAvailableCharacters({ video_id: videoId })

        const taskId =
          response &&
          response.data &&
          (response.data['task-id'] ||
            response.data.task_id ||
            response.data.taskId)

        if (!taskId) {
          throw new Error('Missing task-id in getAvailableCharacters response')
        }

        const charArray = await waitForTaskOutput(taskId)
        if (!Array.isArray(charArray)) {
          throw new Error('Invalid response format: output is not an array')
        }

        const formattedCharacters = charArray
          .filter(Boolean)
          .map((item, index) => ({
            ...item,
            id: index + 1
          }))

        setCharacters(formattedCharacters)
        hasFetchedRef.current = true

        if (onCharactersLoaded) {
          onCharactersLoaded(formattedCharacters)
        }

        return formattedCharacters
      } catch (error) {
        console.error('Error fetching characters:', error)
        return []
      } finally {
        setIsFetchingCharacters(false)
      }
    },
    [isFetchingCharacters, onCharactersLoaded, videoId]
  )

  const resetCharacters = useCallback(() => {
    setCharacters([])
    hasFetchedRef.current = false
  }, [])

  return {
    characters,
    setCharacters,
    isFetchingCharacters,
    fetchCharacters,
    resetCharacters,
    hasFetched: hasFetchedRef.current
  }
}

export default useCharacters
